import { ArrowRight, Check, FileText, FileUp } from 'lucide-react';

import {
    INPUT_MODES,
    PLAIN_TEXT_MAX,
    REQUIREMENT_MAX,
    SCENARIOS,
    TEXT_EXAMPLES,
} from '../constants';
import { countChars } from '../helpers';
import MaterialPanel from './MaterialPanel';
import SettingsPanel from './SettingsPanel';
import UploadZone from './UploadZone';

import styles from '../index.module.scss';

const MODE_ICONS = { text: FileText, file: FileUp };

export default function ContentStep({
    headingRef,
    mode,
    onModeChange,
    prompt,
    onPromptChange,
    requirement,
    onRequirementChange,
    settings,
    onSettingsChange,
    fileTask,
    uploading,
    savingMaterial,
    onUpload,
    onRebuild,
    onReplaceFile,
    onSaveMaterial,
    canNext,
    nextHint,
    onNext,
}) {
    const promptChars = countChars(prompt);
    const over = promptChars > PLAIN_TEXT_MAX;

    return (
        <section className={styles.workspace} aria-labelledby="ppt-content-title">
            <div className={styles.panelHead}>
                <div>
                    <h2 id="ppt-content-title" ref={headingRef} tabIndex={-1}>
                        内容与用途
                    </h2>
                    <p>
                        先选演示用途，再输入研究内容或上传论文。用途决定内容怎么组织，模板只决定视觉风格。
                    </p>
                </div>
            </div>

            <div className={styles.block}>
                <span className={styles.blockLabel} id="ppt-scenario-label">
                    演示用途
                </span>
                <div
                    className={styles.scenarioGrid}
                    role="radiogroup"
                    aria-labelledby="ppt-scenario-label"
                >
                    {SCENARIOS.map((scenario) => {
                        const checked = settings.scenario === scenario.value;
                        return (
                            <button
                                key={scenario.value}
                                type="button"
                                role="radio"
                                aria-checked={checked}
                                className={styles.scenarioCard}
                                onClick={() => onSettingsChange({ scenario: scenario.value })}
                            >
                                <span className={styles.scenarioTitle}>
                                    {scenario.label}
                                    {checked ? <Check size={15} aria-hidden /> : null}
                                </span>
                                <small>{scenario.desc}</small>
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className={styles.block}>
                <div className={styles.modeTabs} role="tablist" aria-label="内容来源">
                    {INPUT_MODES.map((item) => {
                        const Icon = MODE_ICONS[item.value];
                        const selected = mode === item.value;
                        return (
                            <button
                                key={item.value}
                                type="button"
                                role="tab"
                                id={`ppt-mode-${item.value}`}
                                aria-selected={selected}
                                aria-controls={`ppt-panel-${item.value}`}
                                className={styles.modeTab}
                                onClick={() => onModeChange(item.value)}
                            >
                                <Icon size={16} aria-hidden />
                                {item.label}
                            </button>
                        );
                    })}
                </div>

                {mode === 'text' ? (
                    <div
                        role="tabpanel"
                        id="ppt-panel-text"
                        aria-labelledby="ppt-mode-text"
                        className={styles.editor}
                    >
                        <div className={styles.editorHead}>
                            <label htmlFor="ppt-prompt">主题或正文</label>
                            <span>可以只写论文题目，也可以粘贴摘要、各章要点或完整资料</span>
                        </div>
                        <textarea
                            id="ppt-prompt"
                            className={styles.textarea}
                            value={prompt}
                            placeholder="例如：毕业论文题目《基于深度学习的……》，研究方法是……，实验结果显示准确率从 86% 提升到 89%……（资料里没有的数据不会被编造）"
                            onChange={(event) => onPromptChange(event.target.value)}
                        />
                        <div className={styles.editorFoot}>
                            <div className={styles.examples}>
                                <span>试试：</span>
                                {TEXT_EXAMPLES.map((example) => (
                                    <button
                                        key={example.label}
                                        type="button"
                                        className={styles.chip}
                                        onClick={() => onPromptChange(example.text)}
                                    >
                                        {example.label}
                                    </button>
                                ))}
                            </div>
                            <span className={styles.counter} data-over={over}>
                                {over ? '超出上限，更长的资料请改为上传文档 · ' : ''}
                                {promptChars.toLocaleString()} / {PLAIN_TEXT_MAX.toLocaleString()}
                            </span>
                        </div>
                    </div>
                ) : (
                    <div
                        role="tabpanel"
                        id="ppt-panel-file"
                        aria-labelledby="ppt-mode-file"
                        className={styles.filePanel}
                    >
                        {fileTask ? (
                            <MaterialPanel
                                task={fileTask}
                                saving={savingMaterial}
                                onRebuild={onRebuild}
                                onReplace={onReplaceFile}
                                onSave={onSaveMaterial}
                            />
                        ) : (
                            <UploadZone uploading={uploading} onUpload={onUpload} />
                        )}
                        <div className={styles.field}>
                            <label htmlFor="ppt-requirement">额外要求（可选）</label>
                            <textarea
                                id="ppt-requirement"
                                className={styles.smallTextarea}
                                rows={3}
                                maxLength={REQUIREMENT_MAX}
                                value={requirement}
                                placeholder="例如：重点讲研究方法与实验结果，控制在 10 分钟内，不要编造数据"
                                onChange={(event) => onRequirementChange(event.target.value)}
                            />
                        </div>
                    </div>
                )}
            </div>

            <SettingsPanel settings={settings} onChange={onSettingsChange} />

            <div className={styles.foot}>
                <p className={styles.footNote}>{nextHint}</p>
                <div className={styles.footActions}>
                    <button
                        type="button"
                        className={styles.primaryButton}
                        disabled={!canNext}
                        onClick={onNext}
                    >
                        下一步：选择模板
                        <ArrowRight size={16} aria-hidden />
                    </button>
                </div>
            </div>
        </section>
    );
}
